import { KycGroup, Option, Question } from '@/queries/kyc-survey';
import { Checkbox } from '../../forms/components/checkbox';
import { Field } from '../../forms/components/field';
import { Button } from '../../typography/button/button';
import { getCountrySelectionOptions } from './countries';
import { useLocalization } from '@/hooks/use-localization';
import { Select } from '../../forms/components/select';
import { Arrow } from './components/svg';
import { PropsType } from './bank-open-savings-form';
import { useMemo, useState } from 'react';
import { GenericValidationError } from './components/generic-validation-error';

export const StepThree = ({ labels, kycResponse, handleKycChange, kycValues, step, setStep, scrollToFirstValidationErrorElement }: PropsType) => {
    const { getLanguage } = useLocalization();
    const countrySelectOptions = getCountrySelectionOptions(getLanguage());

    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const getButtonOptions = (options?: Option[]) => {
        if (!options?.length) {
            return [];
        }

        return [
            { value: '', label: ' -- Välj -- ' }, // This is only available in Sweden at the moment
            ...options.map(({ text }) => ({ value: text, label: text })),
        ];
    };

    const isConditionMet = (group: KycGroup, displayCondition: { questionId: number; optionId: number }) => {
        const optionText = group.questions
            .find(question => question.id === displayCondition.questionId)
            ?.options?.find(option => option.id === displayCondition.optionId)?.text;

        if (!optionText) {
            return false;
        }

        return kycValues?.[group.id]?.[displayCondition.questionId]?.includes(optionText);
    };

    function renderSingleSectionOptions(question: Question) {
        const options = question.options?.map(({ text }) => ({
            value: text,
            label: text,
        }));

        return options?.length ? [{ value: '', label: question.text }, ...options] : [];
    }

    const validationState = useMemo(() => {
        const requiredFields: Record<string, boolean> = {};

        kycResponse?.result?.kycSurvey.groups.forEach(group => {
            group.questions.forEach(question => {
                let key = null;
                if (question.displayCondition) {
                    if (isConditionMet(group, question.displayCondition)) {
                        key = question.id;
                    }
                } else {
                    key = question.id;
                }

                if (key !== null) {
                    requiredFields[key] = !!kycValues?.[group.id]?.[question.id]?.[0];
                }
            });
        });

        return {
            ...requiredFields,
            allFieldsValid: Object.values(requiredFields).every(Boolean),
        };
    }, [kycResponse, kycValues]);

    const onSubmit = () => {
        if (validationState.allFieldsValid) {
            setStep(step + 1);
            return;
        }

        setShowValidationErrors(true);
        scrollToFirstValidationErrorElement();
    };

    if (!kycResponse?.result) {
        return null;
    }

    return (
        <>
            <div className="r-mb-10 r-mt-4 r-border-b r-pb-10">
                <p>{labels.formStepThreeDescription}</p>

                {showValidationErrors && !validationState.allFieldsValid && <GenericValidationError allRequired />}
            </div>
            <div>
                {kycResponse?.result?.kycSurvey.groups.map((group, index) => {
                    return (
                        <div key={index}>
                            <p className="r-mb-2 r-text-lg r-font-bold md:r-mb-4">{group.title}</p>
                            {group.id === 'TAXES' && <p className="r-pb-6 r-font-medium">{labels.formStepThreeDescription} </p>}
                            {group.id === 'PEP' && <p className="r-pb-6 r-font-medium">{labels.kycPepInfo} </p>}
                            {group.questions
                                .filter(question => {
                                    if (!question.displayCondition) {
                                        return true;
                                    }

                                    return isConditionMet(group, question.displayCondition);
                                })
                                .map((question, index) => (
                                    <div key={index} className="r-mb-4 r-grid r-grid-cols-1 r-gap-4 md:r-grid-cols-2 md:r-gap-10 md:r-py-6">
                                        <p className="r-text-base r-font-bold">{question.text}</p>
                                        <div className="r-space-y-2">
                                            {question.type === 'FREE_TEXT' && (
                                                <Field
                                                    label={question.text}
                                                    value={(kycValues?.[group.id]?.[question.id]?.[0] as string | number | undefined) || ''}
                                                    onChange={v => handleKycChange(group.id, question.id, v, false)}
                                                    showRequiredError={showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]}
                                                />
                                            )}
                                            {question.type === 'COUNTRY_CODE_SELECTION' && (
                                                <Select
                                                    searchable
                                                    label={question.text}
                                                    options={countrySelectOptions}
                                                    value={(kycValues?.[group.id]?.[question.id]?.[0] as string) || ''}
                                                    onChange={v => handleKycChange(group.id, question.id, v as string, false)}
                                                    showRequiredError={showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]}
                                                />
                                            )}
                                            {question.type === 'SINGLE_SELECTION_DROPDOWN' && (
                                                <Select
                                                    label={question.text}
                                                    options={renderSingleSectionOptions(question)}
                                                    value={(kycValues?.[group.id]?.[question.id]?.[0] as string) || ''}
                                                    onChange={v => handleKycChange(group.id, question.id, v as string, false)}
                                                    showRequiredError={showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]}
                                                />
                                            )}
                                            {question.type === 'BUTTON' && (
                                                <Select
                                                    label={question.text}
                                                    value={(kycValues?.[group.id]?.[question.id]?.[0] as string) || ''}
                                                    options={getButtonOptions(question.options)}
                                                    onChange={v => handleKycChange(group.id, question.id, v as string, false)}
                                                    showRequiredError={showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]}
                                                />
                                            )}
                                            {question.options &&
                                                question.options.map((option, index) => {
                                                    const answer = kycValues?.[group.id]?.[question.id] || [];
                                                    switch (question.type) {
                                                        case 'MULTIPLE_SELECTION':
                                                            return (
                                                                <Checkbox
                                                                    key={index}
                                                                    label={option.text}
                                                                    checked={answer.includes(option.text)}
                                                                    showRequiredError={
                                                                        showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]
                                                                    }
                                                                    onChange={isChecked =>
                                                                        handleKycChange(group.id, question.id, option.text, true, isChecked)
                                                                    }
                                                                />
                                                            );
                                                        case 'SINGLE_SELECTION':
                                                            return (
                                                                <Checkbox
                                                                    type="radio"
                                                                    key={index}
                                                                    label={option.text}
                                                                    name={`g-${group.id}-${question.id}`}
                                                                    checked={answer.includes(option.text)}
                                                                    showRequiredError={
                                                                        showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]
                                                                    }
                                                                    onChange={() => handleKycChange(group.id, question.id, option.text, false)}
                                                                />
                                                            );
                                                        case 'COUNTRY_CODE_SELECTION':
                                                            return (
                                                                <Select
                                                                    searchable
                                                                    key={index}
                                                                    value={(answer?.[0] as string) || ''}
                                                                    options={countrySelectOptions}
                                                                    showRequiredError={
                                                                        showValidationErrors && !kycValues?.[group.id]?.[question.id]?.[0]
                                                                    }
                                                                    onChange={v => handleKycChange(group.id, question.id, v as string, false)}
                                                                />
                                                            );
                                                        default:
                                                            return null;
                                                    }
                                                })}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    );
                })}
            </div>
            <div className="r-flex r-justify-center r-gap-4 md:r-gap-8">
                <Button type="secondary" fullWidth onClick={() => setStep(step - 1)}>
                    <div className="r-flex r-space-x-1">
                        <Arrow className="r-w-4 r-rotate-180" />
                        <p>{labels.back}</p>
                    </div>
                </Button>
                <Button type="primary" fullWidth ariaDisabled={!validationState.allFieldsValid} onClick={onSubmit}>
                    <div className="r-flex r-space-x-1">
                        <p>{labels.toSigning}</p>
                        <Arrow className="r-w-4" />
                    </div>
                </Button>
            </div>
        </>
    );
};
